import { uniq } from 'lodash';
import type {
  CleanNode,
  DependencyMapNode,
  DependencyMapReference,
  GroupedTraversals,
  LabelsData,
  ReferenceListReference,
} from '../types';
import { CommonDropdownOptions } from '@ardoq/global-consts';
import { ArdoqId, GroupingRule } from '@ardoq/api-types';
import {
  createGraphItem,
  GraphItem,
  hasDescendantModelId,
  RawGraphNode2,
  type TraversalAndGroupingResult,
  descendantIds,
} from '@ardoq/graph';
import { Features, hasFeature } from '@ardoq/features';
import { isTruthy, type ContextSort } from '@ardoq/common-helpers';
import { GET_CSS_BACKGROUND_OPTIONS } from '../consts';
import { getIconColorFromScopeData } from '../utils';
import { mapReferences } from './mapReferences';
import {
  GlobalReferenceTypeId,
  type EnhancedScopeData,
} from '@ardoq/data-model';
import { calculateTextColors } from './utils';
import type { Locale } from '@ardoq/locale';
import {
  componentUtils,
  colorsAndRepresentations,
  workspaceUtils,
  type FilterColorData,
} from '@ardoq/scope-data';

const isConnectedComponent = (node: DependencyMapNode) =>
  node.children?.length ||
  node.incomingReferences?.length ||
  node.outgoingReferences?.length;

type GetReferencesArgs = {
  nodeId: string;
  referencesByTarget: Record<string, DependencyMapReference[]>;
  referencesBySource: Record<string, DependencyMapReference[]>;
  incomingReferenceTypes: Set<string> | null;
  outgoingReferenceTypes: Set<string> | null;
};
const getReferences = ({
  nodeId,
  referencesByTarget,
  referencesBySource,
  incomingReferenceTypes,
  outgoingReferenceTypes,
}: GetReferencesArgs) => {
  const [incomingReferences, outgoingReferences] = [
    {
      source: referencesByTarget,
      include: incomingReferenceTypes,
    },
    {
      source: referencesBySource,
      include: outgoingReferenceTypes,
    },
  ].map(({ source, include }) => {
    const references = source[nodeId];
    return include?.has(CommonDropdownOptions.ALL_EXCEPT_PARENT_CHILD) ||
      include?.has(CommonDropdownOptions.ALL)
      ? references?.slice()
      : references?.filter(
          ({ referenceTypeId }) =>
            !referenceTypeId || include?.has(referenceTypeId)
        );
  });

  return { incomingReferences, outgoingReferences };
};

export const createReferenceTypeNamesMap = <
  T extends { referenceTypeId: string | null }[],
>(
  referenceList: T,
  scopeData: EnhancedScopeData
) =>
  new Map(
    [...new Set(referenceList.map(({ referenceTypeId }) => referenceTypeId))]
      .filter(isTruthy)
      .map<[string, string | undefined]>(fullReferenceTypeId => {
        const [workspaceId, referenceTypeId] = fullReferenceTypeId.split('-');
        return [
          fullReferenceTypeId,
          workspaceUtils.getReferenceTypeById(
            scopeData,
            workspaceId,
            referenceTypeId
          )?.name,
        ];
      })
      .filter(
        (value: [string, string | undefined]): value is [string, string] =>
          value[1] !== undefined
      )
  );

const createNode = (
  graphItem: GraphItem,
  labelsData: LabelsData,
  isNewConditionalFormatting: boolean | undefined,
  scopeData: EnhancedScopeData,
  filterColorData: FilterColorData
): DependencyMapNode => {
  const { id, modelId } = graphItem;
  const isComponent = componentUtils.isComponent(scopeData, modelId);

  const index = graphItem.isGroup() ? graphItem.id : graphItem.modelId;
  const { mainLabel, subLabel, otherLabels } =
    labelsData.nodeLabelsData.get(index) ?? {};

  const { fill, stroke } =
    componentUtils.getComponentDisplayColorAsSVGAttributes(
      scopeData,
      filterColorData,
      modelId,
      { ...GET_CSS_BACKGROUND_OPTIONS, isNewConditionalFormatting }
    );
  const { textColor, textModerateColor } = calculateTextColors(
    fill,
    isNewConditionalFormatting
  );
  return {
    id,
    modelId,
    isComponent,
    label: mainLabel ?? '',
    subLabel: subLabel ?? null,
    otherLabels,
    className: graphItem.getCSS(GET_CSS_BACKGROUND_OPTIONS),
    fill,
    stroke,
    textColor,
    textModerateColor,
    iconColor: isComponent
      ? getIconColorFromScopeData(
          scopeData,
          filterColorData,
          modelId,
          modelId,
          false,
          isNewConditionalFormatting
        )
      : '',
    representationData: isComponent
      ? colorsAndRepresentations.getComponentRepresentationData(
          scopeData,
          modelId
        )
      : null,
    children: null,
    incomingReferences: null,
    outgoingReferences: null,
    combinedReferences: null,
    referencesOmittedDueToRecursion: false,
    degreeOfParenthood: 0,
    degreeOfChildhood: 0,
    containsCollapsedNodes: false,
  };
};

type BuildNodesArgs = {
  graph: TraversalAndGroupingResult;
  startSet: ArdoqId[];
  referenceList: ReferenceListReference[];
  labelsData: LabelsData;
  incomingDegreesOfRelationship: number;
  outgoingDegreesOfRelationship: number;
  outgoingReferenceTypes: Set<GlobalReferenceTypeId> | null;
  incomingReferenceTypes: Set<GlobalReferenceTypeId> | null;
  showOnlyConnectedComponents: boolean;
  isUserDefinedGrouping: boolean;
  degreesOfParenthood: number;
  strictlyDirectionalConstraint?: boolean;
  traversals?: GroupedTraversals;
  cleanNode: CleanNode;
  degreesOfChildhood?: number;
  contextComponentModelId?: ArdoqId;
  isNewConditionalFormatting?: boolean;
  referenceTypeNames: ReturnType<typeof createReferenceTypeNamesMap>;
  sort: ContextSort;
  locale: Locale;
  scopeData: EnhancedScopeData;
  workspaceIds: ArdoqId[];
  filterColorData: FilterColorData;
  groupBys: GroupingRule[];
  includeOnlyParentsInScope?: boolean;
} & Record<any, any>;

const buildNodes = ({
  graph,
  startSet,
  referenceList,
  labelsData,
  incomingDegreesOfRelationship,
  outgoingDegreesOfRelationship,
  incomingReferenceTypes,
  outgoingReferenceTypes,
  showOnlyConnectedComponents,
  isUserDefinedGrouping,
  degreesOfParenthood,
  strictlyDirectionalConstraint,
  traversals,
  cleanNode,
  contextComponentModelId,
  degreesOfChildhood = Infinity,
  isNewConditionalFormatting,
  referenceTypeNames,
  sort,
  locale,
  scopeData,
  workspaceIds,
  filterColorData,
  groupBys,
  includeOnlyParentsInScope = false,
}: BuildNodesArgs) => {
  const { rootGroups, componentMap, groupMap } = graph;
  const startSetIds = new Set(startSet);
  const componentsAndGroups = [...groupMap.values(), ...componentMap.values()];
  const startSetItems: RawGraphNode2[] = [];
  const startSetRootItems: RawGraphNode2[] = [];
  const graphItemMap = new Map<string, GraphItem>();
  const nodeMap = new Map<string, DependencyMapNode>();

  const rootGroupDescendantIds = new Map(
    rootGroups.map(rootGroup => [rootGroup, new Set(descendantIds(rootGroup))])
  );

  for (const item of componentsAndGroups) {
    if (item.modelId && startSetIds.has(item.modelId)) {
      startSetItems.push(item);
      const rootItem =
        rootGroups.find(rootGroup =>
          rootGroupDescendantIds.get(rootGroup)?.has(item.id)
        ) ?? item;
      startSetRootItems.push(rootItem);
    }
    const graphItem = createGraphItem({ item });
    graphItemMap.set(item.id, graphItem);
    nodeMap.set(
      item.id,
      createNode(
        graphItem,
        labelsData,
        isNewConditionalFormatting,
        scopeData,
        filterColorData
      )
    );
  }

  const rootItems = uniq(
    isUserDefinedGrouping
      ? hasFeature(Features.ROOT_GROUPS_IN_CONTEXT_ONLY) &&
        contextComponentModelId
        ? [
            ...startSetRootItems,
            ...rootGroups.filter(rootGroup =>
              hasDescendantModelId(rootGroup, contextComponentModelId)
            ),
          ]
        : [...startSetRootItems, ...rootGroups]
      : startSetRootItems
  );

  const referencesBySource =
    outgoingDegreesOfRelationship > 0
      ? mapReferences({
          key: 'sourceId',
          nodeMap,
          referenceList,
          degreesOfParenthood,
          referenceTypeNames,
          labelsData,
          isNewConditionalFormatting,
          sort,
          locale,
          scopeData,
          workspaceIds,
          filterColorData,
          groupBys,
          includeOnlyParentsInScope,
        })
      : {};
  const referencesByTarget =
    incomingDegreesOfRelationship > 0
      ? mapReferences({
          key: 'targetId',
          nodeMap,
          referenceList,
          degreesOfParenthood,
          referenceTypeNames,
          labelsData,
          isNewConditionalFormatting,
          sort,
          locale,
          scopeData,
          workspaceIds,
          filterColorData,
          groupBys,
          includeOnlyParentsInScope,
        })
      : {};

  for (const { id, children } of componentsAndGroups) {
    const node = nodeMap.get(id);
    if (!node) {
      continue;
    }
    node.children =
      children
        ?.map(({ id: childId }) => nodeMap.get(childId))
        .filter(isTruthy) ?? null;
    const { incomingReferences, outgoingReferences } = getReferences({
      nodeId: node.id,
      referencesByTarget,
      referencesBySource,
      incomingReferenceTypes,
      outgoingReferenceTypes,
    });
    node.incomingReferences = incomingReferences ?? null;
    node.outgoingReferences = outgoingReferences ?? null;
  }

  const traversalLength =
    (traversals?.incoming?.length ?? 0) + (traversals?.outgoing?.length ?? 0);

  const result: DependencyMapNode[] = [];
  for (const rootItem of rootItems) {
    const node = nodeMap.get(rootItem.id)!;
    const cleanedNode = cleanNode({
      node,
      incomingDegreesOfRelationship:
        traversalLength || incomingDegreesOfRelationship,
      outgoingDegreesOfRelationship:
        traversalLength || outgoingDegreesOfRelationship,
      usedNodes: new Set(),
      strictlyDirectionalConstraint,
      traversals,
      referenceChildMaxDepth: degreesOfChildhood,
    });
    if (!showOnlyConnectedComponents || isConnectedComponent(node)) {
      result.push(cleanedNode);
    }
  }

  return result;
};

export { buildNodes };
